import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityNote } from '@/experiences/sales-portal/components/activity-notes/types';
import { ActivityNotesDesktop } from '@/experiences/sales-portal/components/activity-notes/activity-notes-desktop';
import { ActivityNotesDesktopSkeleton } from '@/experiences/sales-portal/components/activity-notes/activity-notes-desktop-skeleton';
import { ActivityNotesMobile } from '@/experiences/sales-portal/components/activity-notes/activity-notes-mobile';
import { ActivityNotesMobileSkeleton } from '@/experiences/sales-portal/components/activity-notes/activity-notes-mobile-skeleton';
import { ChatProspectStage } from '@/types';
import { CompactMap } from '@/experiences/sales-portal/components/map/compact-map';
import { CompactMapSkeleton } from '@/experiences/sales-portal/components/map/compact-map-skeleton';
import { Coordinate } from '@usepepper/ui/molecules/map/types';
import { createStyles } from '@/contexts/theme-context';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { Map } from '@/experiences/sales-portal/components/map/map';
import { MapMarkerData } from '@/experiences/sales-portal/components/map/types';
import { MapSkeleton } from '@/experiences/sales-portal/components/map/map-skeleton';
import { ProspectDetailAddress } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/types';
import { ProspectDetailHeader } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/components/prospect-detail-header';
import { ProspectDetailHeaderSkeleton } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/components/prospect-detail-header-skeleton';
import {
  QuickActionItem,
  QuickActionsCard,
} from '@/experiences/sales-portal/components/quick-actions-card';
import { useIsMobileBottomNavLayout } from '@sales-portal/blocs/hooks/use-is-mobile-bottom-nav-layout';
import { View } from 'react-native';

interface ProspectDetailLayoutProps {
  accountId: string | null;
  activityNotes: ActivityNote[];
  activityNotesHasError: boolean;
  address: ProspectDetailAddress | null;
  coordinate?: Coordinate;
  fetchingMoreActivityNotes: boolean;
  hasError: boolean;
  isPhoneNumberLoading: boolean;
  isStageLoading: boolean;
  loading: boolean;
  loadingActivityNotes: boolean;
  markers?: MapMarkerData[];
  onBackToProspects: () => void;
  onRetry: () => void;
  onRetryActivityNotes: () => void;
  onSelectLost: () => void;
  onSelectStage: (stage: ChatProspectStage) => void;
  phoneNumber: string | null;
  quickActions: QuickActionItem[];
  quickActionsLoading: boolean;
  restaurantName: string | undefined;
  stage: ChatProspectStage | null;
}

const BACK_TO_PROSPECTS_CTA_BUTTON_WIDTH = 150;
const QUICK_ACTIONS_SKELETON_ROW_COUNT = 5;

export const ProspectDetailLayout: React.FC<ProspectDetailLayoutProps> = ({
  accountId,
  activityNotes,
  activityNotesHasError,
  address,
  coordinate,
  fetchingMoreActivityNotes,
  hasError,
  isPhoneNumberLoading,
  isStageLoading,
  loading,
  loadingActivityNotes,
  markers,
  onBackToProspects,
  onRetry,
  onSelectLost,
  onSelectStage,
  phoneNumber,
  onRetryActivityNotes,
  quickActions,
  quickActionsLoading,
  restaurantName,
  stage,
}) => {
  const [styles] = useStyles();
  const isMobileBottomNavLayout = useIsMobileBottomNavLayout();

  const contentColumnStyle = [
    styles.contentColumn,
    isMobileBottomNavLayout ? styles.fullWidthColumn : null,
  ];

  const activityNotesSkeleton = isMobileBottomNavLayout ? (
    <ActivityNotesMobileSkeleton />
  ) : (
    <ActivityNotesDesktopSkeleton />
  );

  if (loading) {
    return (
      <View style={styles.content}>
        {isMobileBottomNavLayout ? null : <MapSkeleton />}
        <View style={styles.mainLayout}>
          <View style={contentColumnStyle}>
            {isMobileBottomNavLayout ? (
              <View style={styles.heroRow}>
                <CompactMapSkeleton />
                <View style={styles.heroColumn}>
                  <ProspectDetailHeaderSkeleton />
                </View>
              </View>
            ) : (
              <ProspectDetailHeaderSkeleton />
            )}
            {activityNotesSkeleton}
          </View>
        </View>
      </View>
    );
  }

  if (hasError) {
    return (
      <ErrorState
        description={i18n.t('sales_portal.error.description')}
        onPress={onRetry}
        title={i18n.t('sales_portal.error.title')}
      />
    );
  }

  if (!restaurantName) {
    return (
      <ErrorState
        buttonLabel={i18n.t('sales_portal.prospect_detail.back_to_prospects')}
        ctaProps={{ width: BACK_TO_PROSPECTS_CTA_BUTTON_WIDTH }}
        description={i18n.t(
          'sales_portal.prospect_detail.not_found_description',
        )}
        onPress={onBackToProspects}
        title={i18n.t('sales_portal.prospect_detail.not_found_title')}
      />
    );
  }

  const header = (
    <ProspectDetailHeader
      accountId={accountId}
      address={address}
      isPhoneNumberLoading={isPhoneNumberLoading}
      isStageLoading={isStageLoading}
      name={restaurantName}
      onSelectLost={onSelectLost}
      onSelectStage={onSelectStage}
      phoneNumber={phoneNumber}
      stage={stage}
    />
  );

  const showActivityNotesSkeleton =
    loadingActivityNotes && activityNotes.length === 0;

  const activityNotesSection = showActivityNotesSkeleton ? (
    activityNotesSkeleton
  ) : isMobileBottomNavLayout ? (
    <ActivityNotesMobile
      activityNotes={activityNotes}
      hasError={activityNotesHasError}
      loading={fetchingMoreActivityNotes}
      onRetry={onRetryActivityNotes}
    />
  ) : (
    <ActivityNotesDesktop
      activityNotes={activityNotes}
      hasError={activityNotesHasError}
      loading={fetchingMoreActivityNotes}
      onRetry={onRetryActivityNotes}
    />
  );

  return (
    <View style={styles.content}>
      {isMobileBottomNavLayout ? null : (
        <Map coordinate={coordinate} markers={markers} />
      )}
      <View style={styles.mainLayout}>
        <View style={contentColumnStyle}>
          {isMobileBottomNavLayout ? (
            <View style={styles.heroRow}>
              <CompactMap coordinate={coordinate} markers={markers} />
              <View style={styles.heroColumn}>{header}</View>
            </View>
          ) : (
            header
          )}
          {activityNotesSection}
        </View>
        {isMobileBottomNavLayout ? null : (
          <View style={styles.sidebarColumn}>
            <QuickActionsCard
              actions={quickActions}
              loading={quickActionsLoading}
              skeletonRowCount={QUICK_ACTIONS_SKELETON_ROW_COUNT}
            />
          </View>
        )}
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  content: {
    gap: theme.sk.spacing.spaceLarge,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  contentColumn: {
    overflow: 'visible',
    paddingBottom: theme.sk.spacing.spaceMedium,
    width: '60%',
  },
  fullWidthColumn: {
    gap: theme.sk.spacing.spaceXlarge,
    width: '100%',
  },
  heroColumn: {
    flex: 1,
    minWidth: 0,
  },
  heroRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
  },
  mainLayout: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  sidebarColumn: {
    gap: theme.sk.spacing.spaceSmall,
    width: '30%',
  },
}));
